import { useEffect, useRef } from 'react';

/**
 * Keyboard control of the run-sheet, ignored while typing in a field:
 *   ArrowRight / PageDown  stage the next item     ArrowLeft / PageUp  stage the previous
 *   Enter                  send the staged item live (not when a button has focus)
 *   B                      clear the live display
 *   ArrowDown / ArrowUp    next / previous slide (lyric lines, or parts of a long passage)
 *   ?                      show the keyboard shortcuts
 * Handlers are read through a ref so the listener is bound once.
 */
export default function useRunSheetKeys({ sheet, stagePlanItem, sendStagedContentLive, clearLiveContent, nextLyricLine, prevLyricLine, onHelp }) {
  const runSheetKeysRef = useRef(null);
  runSheetKeysRef.current = { sheet, stagePlanItem, sendStagedContentLive, clearLiveContent, nextLyricLine, prevLyricLine, onHelp };

  useEffect(() => {
    const onKey = (e) => {
      const el = e.target;
      const tag = el && el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const h = runSheetKeysRef.current;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        const item = h.sheet.next();
        if (item) { e.preventDefault(); h.stagePlanItem(item); }
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        const item = h.sheet.prev();
        if (item) { e.preventDefault(); h.stagePlanItem(item); }
      } else if (e.key === 'Enter' && tag !== 'BUTTON') {
        e.preventDefault();
        h.sendStagedContentLive();
      } else if (e.key === 'ArrowDown' && h.nextLyricLine) {
        e.preventDefault();
        h.nextLyricLine();
      } else if (e.key === 'ArrowUp' && h.prevLyricLine) {
        e.preventDefault();
        h.prevLyricLine();
      } else if (e.key === '?' && h.onHelp) {
        e.preventDefault();
        h.onHelp();
      } else if (e.key === 'b' || e.key === 'B') {
        h.clearLiveContent();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}
